import { CoreBox, CoreTooltip, CoreTypography } from '@youscience/core';

import { FormNames, Recommender } from '@youscience/commonapp-service-common';

import useCommonAppStore from '@store/CommonAppStore';

import FormStatus from '@components/FormStatus';
import LabelWithDate from '@components/LabelWithDate';

import { formatDateWithFallback } from '@utils/formatter';

import { StyledDivider } from '@components/common.styles';
import { StyledTooltipBox } from '../../PlannerSchoolCard/components/PlannerSchoolCardRecommenders/PlannerSchoolCardRecommenders.styles';
import { StyledCounselor, StyledCounselorReports, StyledJustifiedBox } from './CounselorFormStatus.styles';

import InfoIcon from '@images/info.svg?react';

type RecommenderWithCommonApp = Recommender & {
  isFromCommonApp?: boolean;
};

interface CounselorFormsStatusProps {
  counselor: RecommenderWithCommonApp;
}

export const CounselorFormsStatus = ({ counselor }: CounselorFormsStatusProps) => {
  const { formStatuses } = useCommonAppStore();

  const formOrderMap: Map<string, number> = new Map<string, number>([
    [FormNames.SECONDARY_REPORT, 1],
    [FormNames.FEE_WAIVER, 2],
    [FormNames.EARLY_DECISION, 3],
    [FormNames.EARLY_DECISION2, 4],
    [FormNames.RECOMMENDATION, 5],
    [FormNames.OPTIONAL_REPORT, 6],
    [FormNames.OPTIONAL_REPORT2, 7],
    [FormNames.MIDYEAR_REPORT, 8],
    [FormNames.FINAL_REPORT, 9],
  ]);

  const isFromCommonApp = counselor?.isFromCommonApp ?? false;

  return (
    <StyledCounselor>
      <StyledJustifiedBox>
        <StyledTooltipBox>
          <CoreTypography variant="h6" component="h5">
            Counselor
          </CoreTypography>
          <CoreTooltip
            title="
          Counselors share information about you within the context of your entire class and school community.
          They submit several different forms, including your transcript. The counselor recommendation letter
          provides insights into your academic history. They can also share information about achievements,
          extracurricular accomplishments, and personal interests."
          >
            <CoreTypography>
              <InfoIcon />
            </CoreTypography>
          </CoreTooltip>
        </StyledTooltipBox>

        <CoreBox sx={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <CoreBox>
            <StyledTooltipBox>
              <CoreTypography variant="body2">
                <b>
                  {counselor?.firstName} {counselor?.lastName}
                </b>
              </CoreTypography>
              {isFromCommonApp ? (
                <CoreTooltip title="This counselor was added on Common App's website">
                  <CoreTypography>
                    <InfoIcon />
                  </CoreTypography>
                </CoreTooltip>
              ) : null}
            </StyledTooltipBox>
            <StyledDivider />
          </CoreBox>
          <LabelWithDate
            label="Assigned"
            date={formatDateWithFallback(formStatuses?.counselor?.assignedDate as string)}
          />
        </CoreBox>
      </StyledJustifiedBox>
      <StyledCounselorReports>
        {counselor.forms
          .sort((a, b) => (formOrderMap.get(a.type) ?? 0) - (formOrderMap.get(b.type) ?? 0))
          .map((item) => {
            const formStatus = formStatuses?.counselor?.forms.find((status) => status.name === item.type);

            return (
              <StyledJustifiedBox
                key={`Report${counselor.recommenderId}-${item?.type}`}
                data-testid={`${item.type.replaceAll(' ', '')}`}
              >
                <CoreTypography variant="body2" sx={{ fontWeight: 600 }}>
                  {item?.type}
                </CoreTypography>
                <FormStatus formStatus={formStatus}></FormStatus>
              </StyledJustifiedBox>
            );
          })}
      </StyledCounselorReports>
    </StyledCounselor>
  );
};
